import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Vector3 } from 'three';
import { v4 as uuidv4 } from 'uuid';

import { useGetArticlesBySystemHook } from 'api/queries/articles/articles';
import { useSolveAnchorPlacement } from 'api/queries/forming/forming';
import { GroupType } from 'app/modules/wallforming/components/scenes/objects/articles/types/groupType';
import {
    FRAMI_WEDGE_CLAMP_ARTICLE_NUMBER,
    isFramiWedgeClamp,
    isSupportingConstruction
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { getPoiIndexForArticleInstance } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { buildSolveAnchorPlacementRequest } from 'app/modules/wallforming/components/setting-container/anchor-placement/utils/buildSolveAnchorPlacementRequest';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { PartPlacement } from 'api/models';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { GroupMember } from 'app/modules/wallforming/components/scenes/objects/articles/types/groupMember';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

function findArticleId(
    state: PlacementState,
    predicate: (articleNumber: string | null) => boolean,
    instanceId?: string
) {
    return Object.keys(state.articles).find((articleId) => {
        const article = state.articles[articleId];
        return predicate(article.articleNumber) && (!instanceId || instanceId in article.articleInstances);
    });
}

function toPart(placement: PartPlacement): ArticleInstance['parts'][string] {
    const { position, alignment, scale, rotation } = placement;
    return {
        position: new Vector3(position.x, position.y, position.z),
        alignment: new Vector3(alignment.x, alignment.y, alignment.z),
        scale: new Vector3(scale.x, scale.y, scale.z),
        rotation
    };
}

function createClampInstance(
    state: PlacementState,
    articleId: string,
    placement: PartPlacement,
    poiGuid: string
): ArticleInstance {
    const partNames = state.articles[articleId]?.partNames ?? [];
    const names = partNames.length > 0 ? partNames : [placement.name];
    return { parts: Object.fromEntries(names.map((name) => [name, toPart(placement)])), poiGuids: [poiGuid] };
}

function applyPlacement(instance: ArticleInstance, placement: PartPlacement): ArticleInstance {
    return {
        ...instance,
        parts: Object.fromEntries(Object.keys(instance.parts).map((name) => [name, toPart(placement)]))
    };
}

const getHeight = (instance: ArticleInstance) => Object.values(instance.parts)[0]?.position.z ?? 0;

function getGroupedClamps(state: PlacementState, supportInstanceId: string, wedgeArticleId: string) {
    const wedgeInstances = state.articles[wedgeArticleId]?.articleInstances ?? {};
    return (state.getGroupByInstanceId(supportInstanceId)?.members ?? [])
        .filter((member) => member.articleId === wedgeArticleId && member.instanceId in wedgeInstances)
        .map((member) => ({ instanceId: member.instanceId, instance: wedgeInstances[member.instanceId] }))
        .sort((first, second) => getHeight(first.instance) - getHeight(second.instance));
}

export const useReconcileSupportingConstructionClamps = () => {
    const { t } = useTranslation();
    const solveMutation = useSolveAnchorPlacement();
    const getArticlesBySystem = useGetArticlesBySystemHook();
    const requests = useRef(new Map<string, number>());
    const [searchParams] = useSearchParams();
    const sessionId = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);

    const resolveWedgeArticleId = useCallback(async (): Promise<string> => {
        const loadedId = findArticleId(usePlacementStore.getState(), isFramiWedgeClamp);
        if (loadedId) {
            return loadedId;
        }

        const articles = await getArticlesBySystem(import.meta.env.VITE_DEFAULT_SYSTEM_ID, { filterActive: true });
        const wedge = articles.data?.find((article) => isFramiWedgeClamp(article.articleNumber));
        if (!wedge) {
            throw new Error(
                t('errorSupportingConstructionClampUnavailable', { articleNumber: FRAMI_WEDGE_CLAMP_ARTICLE_NUMBER })
            );
        }
        usePlacementStore
            .getState()
            .batchAddArticles([
                { id: wedge.id, articleNumber: wedge.articleNumber, lifeCycleCode: wedge.lifeCycleCode }
            ]);
        return wedge.id;
    }, [getArticlesBySystem, t]);

    return useCallback(
        async (supportInstanceId: string): Promise<void> => {
            let state = usePlacementStore.getState();
            if (!state.autoPlaceSupportingConstructionClamps) {
                return;
            }

            const supportArticleId = findArticleId(state, isSupportingConstruction, supportInstanceId);
            const supportInstance = supportArticleId
                ? state.articles[supportArticleId].articleInstances[supportInstanceId]
                : undefined;
            const poiIndex = supportArticleId
                ? getPoiIndexForArticleInstance(
                      state.pois,
                      supportArticleId,
                      supportInstanceId,
                      supportInstance?.poiGuids
                  )
                : -1;
            const poiGuid = state.pois[poiIndex]?.poi.poiGuid;
            if (!supportArticleId || !supportInstance || !poiGuid) {
                return;
            }

            const requestId = (requests.current.get(supportInstanceId) ?? 0) + 1;
            requests.current.set(supportInstanceId, requestId);
            const supportSnapshot = JSON.stringify(supportInstance);
            const workspaceRevision = state.workspaceRevision;
            const isStale = (current: PlacementState) =>
                requests.current.get(supportInstanceId) !== requestId ||
                current.workspaceRevision !== workspaceRevision ||
                !current.autoPlaceSupportingConstructionClamps ||
                JSON.stringify(current.articles[supportArticleId]?.articleInstances[supportInstanceId]) !==
                    supportSnapshot;

            try {
                const wedgeArticleId = await resolveWedgeArticleId();
                const response = await solveMutation.mutateAsync({
                    data: buildSolveAnchorPlacementRequest({
                        state: usePlacementStore.getState(),
                        sessionId,
                        systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                        poiIndex,
                        selectedAnchorArticleGuid: wedgeArticleId,
                        targetSupportInstanceId: supportInstanceId
                    })
                });
                state = usePlacementStore.getState();
                if (isStale(state)) {
                    return;
                }
                if (!response.accepted) {
                    toastNotifications.information({
                        title: t('info'),
                        message: response.message || t('errorManualAnchorPlacement')
                    });
                    return;
                }

                // Reuse existing clamps bottom-up so unchanged clamps keep their identity; add or remove only the difference.
                const desiredPlacements = (response.snapPoints ?? [])
                    .filter((point) => point.sourceInstanceId === supportInstanceId)
                    .map((point) => point.placement)
                    .sort((first, second) => first.position.z - second.position.z);
                const existingClamps = getGroupedClamps(state, supportInstanceId, wedgeArticleId);
                const retainedClamps = existingClamps.slice(0, desiredPlacements.length);
                const removedIds = existingClamps.slice(desiredPlacements.length).map((clamp) => clamp.instanceId);
                const updates = desiredPlacements.map((placement, index) => {
                    const retained = retainedClamps[index];
                    return retained
                        ? {
                              articleId: wedgeArticleId,
                              instanceId: retained.instanceId,
                              instance: applyPlacement(retained.instance, placement)
                          }
                        : {
                              articleId: wedgeArticleId,
                              instanceId: uuidv4(),
                              instance: createClampInstance(state, wedgeArticleId, placement, poiGuid)
                          };
                });

                state.runHistoryTransaction('historyPlace', () => {
                    if (updates.length > 0) {
                        state.updateArticleInstances(updates, false);
                    }
                    if (removedIds.length > 0) {
                        state.removeArticleInstances({ [wedgeArticleId]: removedIds }, true);
                    }

                    const members: GroupMember[] = [
                        { articleId: supportArticleId, instanceId: supportInstanceId },
                        ...updates.map(({ articleId, instanceId }) => ({ articleId, instanceId }))
                    ];
                    const latestState = usePlacementStore.getState();
                    const groupName = t('labelAutoPlaceSupportingConstructionClamps');
                    const existingGroup = latestState.getGroupByInstanceId(supportInstanceId);
                    if (existingGroup) {
                        latestState.updateArticleGroup(existingGroup.id, groupName, members, existingGroup.color);
                    } else if (members.length > 1) {
                        latestState.createArticleGroup(groupName, members, undefined, GroupType.AUTOMATIC);
                    }
                });
            } catch (error) {
                toastNotifications.error({
                    title: t('error'),
                    message: error instanceof Error ? error.message : t('errorManualAnchorPlacement')
                });
            }
        },
        [sessionId, solveMutation, resolveWedgeArticleId, t]
    );
};
